import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sidebar } from "primereact/sidebar";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import type { MenuItem } from "primereact/menuitem";
import { useAuth } from "../../contexts/AuthContext";

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();

  const menuItems: MenuItem[] = [
    {
      label: "Events",
      icon: "pi pi-calendar",
      command: () => {
        void navigate("/events");
        setSidebarVisible(false);
      },
      className: location.pathname.startsWith("/events")
        ? "p-menuitem-active"
        : "",
    },
    {
      label: "Tags",
      icon: "pi pi-tag",
      command: () => {
        void navigate("/tags");
        setSidebarVisible(false);
      },
      className: location.pathname === "/tags" ? "p-menuitem-active" : "",
    },
    {
      label: "Pending Bookings",
      icon: "pi pi-clock",
      command: () => {
        void navigate("/checkouts/pending");
        setSidebarVisible(false);
      },
      className:
        location.pathname === "/checkouts/pending"
          ? "p-menuitem-active"
          : "",
    },
    {
      label: "All Bookings",
      icon: "pi pi-shopping-cart",
      command: () => {
        void navigate("/checkouts");
        setSidebarVisible(false);
      },
      className:
        location.pathname.startsWith("/checkouts") &&
        location.pathname !== "/checkouts/pending"
          ? "p-menuitem-active"
          : "",
    },
    {
      label: "Ad hoc payments",
      icon: "pi pi-credit-card",
      command: () => {
        void navigate("/payments");
        setSidebarVisible(false);
      },
      className: location.pathname.startsWith("/payments")
        ? "p-menuitem-active"
        : "",
    },
    {
      label: "Users",
      icon: "pi pi-users",
      command: () => {
        void navigate("/users");
        setSidebarVisible(false);
      },
      className: location.pathname.startsWith("/users")
        ? "p-menuitem-active"
        : "",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="p-3 bg-white border-b border-gray-200 flex items-center justify-between md:hidden sticky top-0 z-10">
        <h1 className="text-lg font-semibold text-gray-900">Admin</h1>
        <div className="flex gap-2">
          <Button
            icon="pi pi-sign-out"
            onClick={() => {
              void signOut();
            }}
            className="p-button-text"
            aria-label="Sign out"
          />
          <Button
            icon="pi pi-bars"
            onClick={() => setSidebarVisible(true)}
            className="p-button-text"
            aria-label="Menu"
          />
        </div>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 bg-white border-r border-gray-200">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h1 className="text-xl font-semibold text-gray-900">Admin</h1>
          <Button
            icon="pi pi-sign-out"
            onClick={() => {
              void signOut();
            }}
            className="p-button-text p-button-sm"
            tooltip="Sign out"
            tooltipOptions={{ position: "bottom" }}
            aria-label="Sign out"
          />
        </div>
        <nav className="flex-1 p-4 overflow-y-auto">
          <Menu model={menuItems} className="w-full border-none" />
        </nav>
      </div>

      {/* Mobile Sidebar */}
      <Sidebar
        visible={sidebarVisible}
        onHide={() => setSidebarVisible(false)}
        className="md:hidden"
        position="left"
      >
        <div className="p-4 border-b border-gray-200 mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Admin</h2>
          <Button
            icon="pi pi-sign-out"
            onClick={() => {
              void signOut();
              setSidebarVisible(false);
            }}
            className="p-button-text p-button-sm"
            aria-label="Sign out"
          />
        </div>
        <Menu model={menuItems} className="w-full border-none" />
      </Sidebar>

      {/* Main Content */}
      <main className="md:ml-64 p-4 md:p-6 lg:p-8">{children}</main>
    </div>
  );
}
